import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import { 
  Typography, 
  Paper,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Box,
  Skeleton
} from '@mui/material';
import { API_URI, END_POINTS, ALERTS } from '../Constants';
import { CandidateContext } from '../Context';
import { datetimeCoverter } from './UsefulComponents';
import axiosInstance from '../../Api/Instance';
import { useSelector } from 'react-redux';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ShareIcon from '@mui/icons-material/Share';
import EmailIcon from '@mui/icons-material/Email';
import AssignmentIcon from '@mui/icons-material/Assignment';

const SharedByMe = () => {
  const [sharedList, setSharedList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [orderBy, setOrderBy] = useState('sharedAt');
  const [order, setOrder] = useState('desc');
  
  const { CANDIDATE } = END_POINTS;
  const user = useSelector((state) => state.auth.user);
  const userId = user?._id;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosInstance.get(`/shared-by-me/${userId}`);
        setSharedList(response.data.data || []);
      } catch (error) {
        console.error('Failed to fetch shared candidates:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  const handleViewProfile = async (candidateId) => {
    try {
      const response = await axiosInstance.get(`${CANDIDATE}/${candidateId}`);
      const data = response.data;

      navigate(`${CANDIDATE}/${candidateId}`, {
        state: { candidate: data },
      });
    } catch (error) {
      console.error(`${ALERTS.HTTP_ERROR} :`, error?.response?.status || error.message);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getStatusChipColor = (status) => {
    switch (status) {
      case 'Pending': return { bg: '#FEF3C7', text: '#92400E', border: '#F59E0B' };
      case 'Reviewed': return { bg: '#D1FAE5', text: '#065F46', border: '#10B981' };
      case 'On Hold': return { bg: '#FECACA', text: '#991B1B', border: '#EF4444' };
      case 'Shared': return { bg: '#DBEAFE', text: '#1E40AF', border: '#3B82F6' };
      default: return { bg: '#F3F4F6', text: '#374151', border: '#9CA3AF' };
    }
  };

  const sortedData = React.useMemo(() => {
    const comparator = (a, b) => {
      if (orderBy === 'sharedAt') {
        return order === 'desc' 
          ? new Date(b.sharedAt) - new Date(a.sharedAt)
          : new Date(a.sharedAt) - new Date(b.sharedAt);
      }
      if (orderBy === 'name') {
        const nameA = a.candidateId?.name || '';
        const nameB = b.candidateId?.name || '';
        return order === 'desc'
          ? nameB.localeCompare(nameA)
          : nameA.localeCompare(nameB);
      }
      if (orderBy === 'status') {
        return order === 'desc'
          ? (b.status || '').localeCompare(a.status || '')
          : (a.status || '').localeCompare(b.status || '');
      }
      return 0;
    };

    return [...sharedList].sort(comparator);
  }, [sharedList, order, orderBy]);

  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 w-full">
  <div className="px-4 md:px-8 py-6 w-[100%]">
        
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1 h-8 bg-gradient-to-b from-green-500 to-blue-500 rounded-full"></div>
            <Typography 
              variant="h4" 
              className="font-bold bg-gradient-to-r from-green-700 to-blue-700 bg-clip-text text-transparent"
            >
              Candidates Shared By Me
            </Typography>
          </div>
          <Typography variant="body1" className="text-gray-600 ml-4">
            Track and manage candidates you've shared with other team members
          </Typography>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {isLoading
            ? Array(4).fill(0).map((_, i) => (
                <Card key={i} className="shadow-lg border-0">
                  <CardContent className="p-4">
                    <Skeleton variant="rectangular" height={50} width={50} className="mb-3 rounded-lg" />
                    <Skeleton width="60%" />
                  </CardContent>
                </Card>
              ))
            : [
                { label: 'Total Shared', value: sharedList?.length || 0, color: 'from-blue-500 to-blue-600' },
                { label: 'Pending Review', value: sharedList?.filter(c => c.status === 'Pending').length || 0, color: 'from-yellow-500 to-yellow-600' },
                { label: 'Reviewed', value: sharedList?.filter(c => c.status === 'Reviewed').length || 0, color: 'from-green-500 to-green-600' },
                { label: 'On Hold', value: sharedList?.filter(c => c.status === 'On Hold').length || 0, color: 'from-red-500 to-red-600' }
              ].map((stat, index) => (
                <Card key={index} className="shadow-lg border-0 hover:shadow-xl transition-shadow">
                  <CardContent className="p-4">
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${stat.color} flex items-center justify-center mb-3`}>
                      <Typography variant="h6" className="text-white font-bold">
                        {stat.value}
                      </Typography>
                    </div>
                    <Typography variant="body2" className="text-gray-600 font-medium">
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              ))
          }
        </div>

        {/* Table Section */}
        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <ShareIcon className="text-gray-600" />
              <Typography variant="h6" className="font-semibold text-gray-800">
                Shared Candidates Overview
              </Typography>
            </div>

            {isLoading ? (
              <Box>
                {Array(8).fill(0).map((_, i) => (
                  <Box key={i} className="flex justify-between items-center mb-3 p-2 border-b border-gray-200">
                    <Skeleton width="20%" />
                    <Skeleton width="20%" />
                    <Skeleton width="15%" />
                    <Skeleton width="25%" />
                    <Skeleton width="15%" />
                  </Box>
                ))}
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} className="border border-gray-200 rounded-lg">
                  <Table sx={{ minWidth: 800 }}>
                    <TableHead>
                      <TableRow className="bg-gray-50">
                        <TableCell className="font-semibold text-gray-700">
                          <TableSortLabel
                            active={orderBy === 'name'}
                            direction={orderBy === 'name' ? order : 'asc'}
                            onClick={() => handleRequestSort('name')}
                          >
                            Candidate Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell className="font-semibold text-gray-700">Shared With</TableCell>
                        <TableCell className="font-semibold text-gray-700">
                          <TableSortLabel
                            active={orderBy === 'status'}
                            direction={orderBy === 'status' ? order : 'asc'}
                            onClick={() => handleRequestSort('status')}
                          >
                            Status
                          </TableSortLabel>
                        </TableCell>
                        <TableCell className="font-semibold text-gray-700">Remarks</TableCell>
                        <TableCell className="font-semibold text-gray-700">
                          <TableSortLabel
                            active={orderBy === 'sharedAt'}
                            direction={orderBy === 'sharedAt' ? order : 'asc'}
                            onClick={() => handleRequestSort('sharedAt')}
                          >
                            Shared At
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedData.map((share, index) => {
                        const colors = getStatusChipColor(share.status);
                        return (
                          <TableRow 
                            key={share._id}
                            className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                          >
                            <TableCell>
                              <Box 
                                className="flex items-center gap-2 cursor-pointer" 
                                onClick={() => handleViewProfile(share.candidateId?._id)}
                              >
                                <PersonIcon className="text-blue-600 text-lg" />
                                <Typography 
                                  className="text-blue-700 hover:text-blue-900 font-semibold hover:underline transition-colors"
                                  sx={{ fontSize: 14 }}
                                >
                                  {share.candidateId?.name ?? 'Unnamed'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box className="flex items-center gap-2">
                                <EmailIcon className="text-gray-500 text-sm" />
                                <Typography className="text-gray-700 font-medium" sx={{ fontSize: 14 }}>
                                  {share?.panelistId?.email ?? 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={share?.status || 'N/A'}
                                size="small"
                                sx={{
                                  backgroundColor: colors.bg,
                                  color: colors.text,
                                  border: `1px solid ${colors.border}`,
                                  fontWeight: 600,
                                  fontSize: 12,
                                  height: 24
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box className="flex items-center gap-2">
                                <AssignmentIcon className="text-gray-500 text-sm" />
                                <Typography className="text-gray-600" sx={{ fontSize: 13 }}>
                                  {share?.remarks || '—'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box className="flex items-center gap-1">
                                <AccessTimeIcon className="text-gray-500 text-sm" />
                                <Typography className="text-gray-600" sx={{ fontSize: 13 }}>
                                  {datetimeCoverter(new Date(share.sharedAt))}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  <TablePagination
                    rowsPerPageOptions={[5, 8, 10, 25]}
                    component="div"
                    count={sharedList.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    className="border-t border-gray-200 bg-gray-50"
                  />
                </TableContainer>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SharedByMe;
